// composables/useChatEventHandlers.js - FIXED WITH AGENT JOIN PREVENTION
import { escalateToHuman } from "../lib/appwriteChat";
import { chatStateManager } from "../lib/chatStateManager";

export function useChatEventHandlers(
  addMessage,
  transitionToPhase,
  startAgentTimeoutTimer,
  clearAgentTimeoutTimer,
  handleTimeoutResolved
) {
  const handleFormSubmitted = (data) => {
    console.log("📋 Form submitted:", data);

    chatStateManager.startNewChat(
      data.customer,
      data.conversation,
      data.formData.name
    );

    return {
      customer: data.customer,
      conversation: data.conversation,
      customerName: data.formData.name,
    };
  };

  const handleMoodSubmitted = (moodData) => {
    console.log("😊 Mood feedback received:", moodData);

    addMessage({
      type: "ai",
      content: "Vielen Dank für dein Feedback! Der Chat wird geschlossen.",
    });

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(moodData);
      }, 2000);
    });
  };

  const handleEscalateToHuman = async (escalationData = {}) => {
    console.log("🆘 Escalating to human agent:", escalationData);

    startAgentTimeoutTimer();

    const state = chatStateManager.getState();
    if (state.conversation) {
      try {
        await escalateToHuman(
          state.conversation.$id,
          escalationData.reason || "Customer request"
        );
        console.log("✅ Conversation escalated in database");
      } catch (error) {
        console.error("❌ Error escalating conversation:", error);
      }
    }

    setTimeout(() => {
      transitionToPhase("human");
      console.log("🤝 Transitioned to human phase");
    }, 1000);
  };

  // ✅ ENHANCED: Agent joined handler with duplicate prevention
  const handleAgentJoined = (agentData) => {
    console.log("👤 Agent joined event received:", agentData);

    // ✅ CRITICAL: Check if agent already joined to prevent duplicates
    if (chatStateManager.hasAgentJoined()) {
      console.log("🚫 Agent already marked as joined - skipping duplicate join message");
      return agentData; // Still return the data but don't add message
    }

    // ✅ Check if this specific agent ID was already processed
    if (agentData.agentInfo?.agentId && chatStateManager.isAgentIdProcessed(agentData.agentInfo.agentId)) {
      console.log("🚫 Agent ID already processed - skipping duplicate:", agentData.agentInfo.agentId);
      return agentData;
    }

    // ✅ Check if we already have an agent-joined message in the current messages
    const currentState = chatStateManager.getState();
    const hasAgentJoinMessage = currentState.messages.some(msg => msg.type === 'agent-joined');
    
    if (hasAgentJoinMessage) {
      console.log("🚫 Agent join message already exists in messages - skipping duplicate");
      // Mark as joined but don't add another message
      chatStateManager.markAgentAsJoined(agentData.agentInfo);
      return agentData;
    }

    console.log("✅ Adding agent joined message - first time for this agent");

    clearAgentTimeoutTimer();
    handleTimeoutResolved();

    // ✅ Get agent name with fallback
    const agentName = agentData.agentInfo?.agentName || "Ein Mitarbeiter";

    // ✅ Add the agent joined message
    addMessage({
      type: "agent-joined",
      content: `👤 ${agentName} ist dem Chat beigetreten`,
      subtitle: "Sie können jetzt direkt mit unserem Team sprechen",
      agentInfo: agentData.agentInfo,
      timestamp: new Date(),
      id: `agent-join-${Date.now()}`,
    });

    console.log("✅ Agent joined system message added to chat");

    return agentData;
  };

  const handleRatingSubmitted = (ratingData) => {
    console.log("⭐ Rating submitted:", ratingData);

    addMessage({
      type: "system",
      content: `Bewertung: ${ratingData.rating}/5 Sterne - Vielen Dank!`,
    });

    return ratingData;
  };

  const handleRatingSkipped = () => {
    console.log("⏭️ Rating skipped");

    addMessage({
      type: "system",
      content: "Bewertung übersprungen - Vielen Dank für Ihren Besuch!",
    });
  };

  const handleChatComplete = () => {
    console.log("🏁 Chat completed");
    chatStateManager.endChat();
  };

  const handleEmailSent = (emailData) => {
    console.log("📧 Email sent for timeout:", emailData);

    addMessage({
      type: "system",
      content: "E-Mail wurde gesendet. Wir melden uns bald bei Ihnen.",
    });

    handleTimeoutResolved();
    return emailData;
  };

  // ✅ ENHANCED: Complete restart with agent tracking reset
  const handleRestartChat = () => {
    console.log("🔄 Restarting chat - clearing all state and subscriptions");

    // Clear all timers first
    clearAgentTimeoutTimer();
    handleTimeoutResolved();

    // ✅ CRITICAL: Signal that all monitoring should stop
    console.log("🛑 Initiating complete restart with subscription cleanup");

    // Use the complete restart method from state manager (already includes agent tracking reset)
    chatStateManager.completeRestart();

    // ✅ CRITICAL: Force transition to start phase AFTER restart
    setTimeout(() => {
      transitionToPhase("start");
      console.log("🏠 Forced transition to start phase after restart");
    }, 300); // Give time for cleanup

    console.log("✅ Chat restarted successfully - agent tracking reset");

    return {
      action: "restart",
      nextPhase: "start",
      shouldReset: true,
      stopAllSubscriptions: true, // ✅ Signal to stop all subscriptions
    };
  };

  return {
    handleFormSubmitted,
    handleMoodSubmitted,
    handleEscalateToHuman,
    handleAgentJoined, // ✅ Enhanced with duplicate prevention
    handleRatingSubmitted,
    handleRatingSkipped,
    handleChatComplete,
    handleEmailSent,
    handleRestartChat, // ✅ Enhanced restart handler
  };
}